import React, { useEffect, useState } from 'react';
import { Popover } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { toast } from 'react-toastify';
import { dayjs } from '../../../utils/dayjs-utils';
import { addLogStory } from '../../../services/inbound.service';
import { useFormValidation } from '../../../utils/useFormValidation';
import { logScreenshotSchema } from '../../../utils/validations';
import DropZoneInput from '../../common/DropZoneInput/DropZoneInput';
import { Spacer } from '../../common';
import InputComponent from '../../common/InputComponent/InputComponent';
import CalenderIcon from '../../../assets/icons/CalenderIcon';
import TextButton from '../../common/TextButton/TextButton';
import AddLogPostBottomComponent from './AddLogPostBottomComponent';

interface AddScreenshotLog {
  postedDate: Date;
  selectedFiles: any[];
}

interface Props {
  onClose: () => any;
  refetchLogPosts: () => any;
  creatorDetails: Record<any, any>;
}

function AddLogPostScreenshot({ onClose, creatorDetails, refetchLogPosts }: Props) {
  const [formValues, setFormValues] = useState<AddScreenshotLog>({ postedDate: new Date(), selectedFiles: [] });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);

  const { validate, errors, setErrors } = useFormValidation<AddScreenshotLog>(logScreenshotSchema);

  const onFileDrop = (acceptedFiles: any[]) => {
    setErrors({});
    if (acceptedFiles.length + formValues.selectedFiles.length > 4) {
      toast.error('Please select only 4 files.');
      return;
    }
    acceptedFiles.forEach((file) => {
      const fileSize = (file.size / 1024) * 0.001;
      // check file size should be less than 5mb
      if (fileSize > 5) {
        toast.error('Max size is 5mb');
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.addEventListener('load', () => {
        setFormValues((p) => ({
          ...p,
          selectedFiles: [
            ...p.selectedFiles,
            {
              file,
              id: Date.now().toString(),
              filename: file.name,
              contentType: file.type,
              path: reader.result,
              preview: URL.createObjectURL(file),
            },
          ],
        }));
      });
    });
  };

  const onRemoveImage = (index: number) => {
    const fileToRemove = formValues.selectedFiles[index];
    if (fileToRemove && fileToRemove.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }
    setFormValues((p) => ({ ...p, selectedFiles: p.selectedFiles.filter((_, idx) => idx !== index) }));
  };

  useEffect(() => {
    return () => {
      formValues.selectedFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [formValues.selectedFiles]);

  const onFormSubmit = async () => {
    const formData = new FormData();
    const isValid = validate({ postedDate: formValues.postedDate });

    if (!isValid) return;
    if (formValues.selectedFiles.length === 0) {
      setErrors((p) => ({ ...p, selectedFiles: 'Please upload atleast one file.' }));
      return;
    }
    formData.append('postedDate', formValues.postedDate.toISOString());
    formData.append('influencerId', creatorDetails._id);

    formValues.selectedFiles.forEach((file) => {
      formData.append('files', file.file);
    });

    setSubmittingForm(true);
    const res = await addLogStory(formData, {});

    if (res.status) {
      refetchLogPosts();
      toast.success('Story logged successfully.');
      onClose();
    } else {
      toast.error('Error logging post.');
    }
    setSubmittingForm(false);
  };

  return (
    <div>
      <DropZoneInput
        onRemove={onRemoveImage}
        onDrop={onFileDrop}
        selectedFiles={formValues.selectedFiles}
        label="Upload screenshots of post/story (upto 4)"
        required
        error={errors.selectedFiles}
        onDropRejected={(e) => {
          if (e[0]?.errors[0]?.code === 'too-many-files') {
            toast.error('Please select only 4 files.');
          }
        }}
      />
      <Spacer spacing="16px" />
      <Popover opened={isDatePickerOpen} onChange={setIsDatePickerOpen} position="top-end" offset={-26}>
        <Popover.Target>
          <InputComponent
            disabled={submittingForm}
            error={errors.postedDate}
            readOnly
            left={<CalenderIcon />}
            right={<TextButton text="Change" onClick={() => setIsDatePickerOpen((v) => !v)} />}
            label="Posted Date"
            value={dayjs(formValues.postedDate).format('DD/MM/YYYY')}
            onChange={() => {}}
          />
        </Popover.Target>
        <Popover.Dropdown>
          <DatePicker
            value={formValues.postedDate}
            maxDate={new Date()}
            onChange={(v) => {
              setErrors({});
              setFormValues((p) => ({ ...p, postedDate: v! }));
            }}
          />
        </Popover.Dropdown>
      </Popover>
      <Spacer spacing="24px" />
      <AddLogPostBottomComponent isLoading={submittingForm} onClose={onClose} onSubmit={onFormSubmit} />
    </div>
  );
}

export default AddLogPostScreenshot;
